import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowDownCircle, ArrowLeftRight, ArrowUpCircle, CheckCircle2, ChevronLeft,
  ChevronRight, Clock3, Coins, Download, FileText, Gamepad2, Inbox, Loader2,
  RefreshCw, Scale, Search, Wallet, X, XCircle,
} from 'lucide-react';
import client, { createIdempotencyKey } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import ConfirmDialog from './components/ConfirmDialog';
import DateRangeFilter from './components/DateRangeFilter';
import FinanceMethodReport from './components/FinanceMethodReport';
import TransactionDetailDrawer from './components/TransactionDetailDrawer';
import { AdminError } from './components/PageState';
import { EMPTY_DATE_RANGE, dateRangeParams } from './lib/dateRange';
import { financeDecision } from './lib/financeDecision';
import {
  FINANCE_PROVIDER_OPTIONS, financeProviderLabel,
} from './lib/financeReport';
import PageSizeSelect from './components/PageSizeSelect';
import { readPageSize, writePageSize } from './lib/pagination';
import { useAdminPermissions } from './lib/permissions';
import {
  formatDateTime, formatMoney, formatNumber, transactionStatusLabel,
  transactionTypeLabel,
} from './lib/formatters';
import {
  payoutDescription, transactionActor, visibleTransactionNote,
} from './lib/transactions';
import './finance.css';

export const financeTabs = [
  { id: 'all', label: 'Tümü' },
  { id: 'deposit', label: 'Yatırımlar' },
  { id: 'withdraw', label: 'Çekimler' },
];

// Durum süzgeci sekmelerle aynı geometridedir; rengi durumun kendisidir
// (bekleyen amber, onaylı yeşil, reddedilen kırmızı) — liste satırındaki
// rozetle aynı dil.
export const financeStatuses = [
  { id: 'all', label: 'Tüm durumlar', tone: '' },
  { id: 'beklemede', label: 'Bekleyen', tone: 'is-wait' },
  { id: 'onaylandi', label: 'Onaylı', tone: 'is-in' },
  { id: 'reddedildi', label: 'Reddedilen', tone: 'is-out' },
];

const STATUS_IDS = financeStatuses.map((item) => item.id);

// Türü sabit talep sayfaları: sol
// menüde Dashboard'un altında ayrı ayrı durur ve aynı listenin türü kilitli
// hâlidir. Ayrı bir liste yazılmaz — onay/ret, sağlayıcı sorgusu ve detay
// paneli Finans Geçmişi'yle aynı kalmalıdır. Varsayılan durum "Bekleyen"dir:
// sayfanın işi karar bekleyen kuyruktur.
export const FINANCE_REQUEST_PAGES = {
  deposit: {
    title: 'Para Yatırma Talepleri',
    icon: ArrowDownCircle,
    description: 'Üyelerin yatırım talepleri. Varsayılan görünüm karar '
      + 'bekleyenlerdir; durum süzgeciyle onaylanan ve reddedilenlere de '
      + 'bakılabilir.',
  },
  withdraw: {
    title: 'Para Çekme Talepleri',
    icon: ArrowUpCircle,
    description: 'Üyelerin çekim talepleri. Varsayılan görünüm karar '
      + 'bekleyenlerdir; onaydan önce satırdaki oyun simgesiyle üyenin bahis '
      + 'geçmişine bakılabilir.',
  },
};

const iconMap = {
  deposit: ArrowDownCircle,
  withdraw: ArrowUpCircle,
};

const stateToneMap = {
  onaylandi: 'is-ok',
  beklemede: 'is-wait',
  reddedildi: 'is-bad',
};

// Sağlayıcı işlem durumunun panelde okunabilir karşılığı; liste satırının
// altına yazılır çünkü "beklemede" tek başına yetmiyor — yöneticinin sorusu
// çoğu zaman "kimde bekliyor" oluyor.
export const PROVIDER_STATE_LABELS = {
  completed: 'Sağlayıcıda tamamlandı',
  approved: 'Sağlayıcıda onaylandı',
  onay_bekliyor: 'Yönetici onayı bekliyor',
  creating: 'Sağlayıcıya iletiliyor',
  pending: 'Sağlayıcıda bekliyor',
  success: 'Sağlayıcıda başarılı',
  fail: 'Sağlayıcıda başarısız',
  failed: 'Sağlayıcı reddetti',
  not_found: 'Sağlayıcıda kayıt bulunamadı',
  expired: 'Ödeme süresi doldu',
  unknown: 'Sonuç belirsiz, mutabakat bekliyor',
};

// Sayfa boyutu yöneticinin seçimidir; bu değer yalnızca tercih
// okunamadığında kullanılan varsayılandır (bkz. lib/pagination).
const PAGE_SIZE = 50;

// Canlı takip. Varsayılan kapalıdır ve tercih yöneticinin kendi
// tarayıcısında durur: sunucuya yazılsaydı bir yöneticinin seçimi
// diğerininkini değiştirirdi (Dashboard'un dönem seçimiyle aynı yaklaşım).
//
// Liste ucu (sayfa + toplam + özet kartları) birkaç saniyede bir
// çağrılamayacak kadar ağırdır. Canlı takip bu yüzden iki hızda döner:
// `LIVE_PULSE_MS`'de bir yalnızca değişiklik imzası sorulur
// (`/admin/transactions/pulse`, iki indeks okuması) ve imza değişince liste
// hemen okunur; `LIVE_REFRESH_MS` ise imzanın göremediği değişiklikler
// (sağlayıcı durumu, onaylı kaydın ret'e çevrilmesi) için seyrek tam okumadır.
// Tur bir süre yalnızca 20 saniyelik tam okumaydı ve yeni talep ekrana
// ortalama on saniye gecikmeyle düşüyordu — "canlı" düğmesi anlık değildi.
export const LIVE_PULSE_MS = 3000;
export const LIVE_REFRESH_MS = 60000;
export const LIVE_STORAGE_KEY = 'admin:finance-live';

export const readStoredLive = () => {
  try {
    return window.localStorage.getItem(LIVE_STORAGE_KEY) === 'on';
  } catch (_) {
    // Gizli sekmede ve depolama kapalı tarayıcıda okuma hata fırlatır; sayfa
    // bunun yüzünden açılmamazlık etmemelidir.
    return false;
  }
};

const storeLive = (value) => {
  try {
    window.localStorage.setItem(LIVE_STORAGE_KEY, value ? 'on' : 'off');
  } catch (_) {
    // Tercihin saklanamaması sayfayı bozmaz; seçim o oturum için geçerlidir.
  }
};

// Damga saniyeyi de yazar: canlı takip birkaç saniyede bir okuduğunda
// dakika hassasiyetindeki bir damga yerinde sayıyor gibi görünür ve yönetici
// listenin tazelendiğini göremezdi.
export const formatStamp = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('tr-TR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

// Sağlayıcılı çekimde onayın ne anlama geldiğini onay penceresinde de yazar:
// para o an ödenmez, talep sağlayıcıya açılır.
const providerHandoffNote = (decision) => {
  if (!decision?.item) return '';
  const ability = financeDecision(decision.item);
  // Not yalnızca sağlayıcıya henüz iletilmemiş çekim için doğrudur; iletilmiş
  // bir kaydı elle kapatmak sağlayıcıya hiçbir şey göndermez.
  if (!ability.providerManaged || !ability.awaitingApproval) return '';
  return decision.action === 'approve'
    ? ` Talep ${ability.providerLabel}'a iletilecek; ödemenin sonucunu sağlayıcı bildirecek.`
    : ` Talep ${ability.providerLabel}'a hiç iletilmeyecek, tutar oyuncuya iade edilecek.`;
};

// Sık yazılan red sebepleri. Tıklanınca alana yazılır, yönetici düzenleyebilir;
// sebep üyenin gelen kutusuna ve Telegram bildirimine olduğu gibi gider.
export const REJECT_REASON_PRESETS = [
  'Çevrim şartı tamamlanmadı.',
  'IBAN sahibi hesap sahibiyle uyuşmuyor.',
  'Hesap bilgileri hatalı, lütfen kontrol edip yeniden talep oluşturun.',
  'Kimlik doğrulaması tamamlanmadan çekim yapılamaz.',
];
export const REJECT_REASON_MAX = 500;

export const buildFinanceParams = ({
  skip, tab, status, query, dateRange, limit = PAGE_SIZE,
  provider = 'all', method = '',
}) => {
  const params = { limit, skip, scope: 'finance' };
  if (tab !== 'all') params.type = tab;
  if (status !== 'all') params.status = status;
  if (query.trim()) params.q = query.trim();
  if (provider && provider !== 'all') params.provider = provider;
  if (method) params.method = method;
  return { ...params, ...dateRangeParams(dateRange) };
};

/**
 * Özet kartların okunuşu.
 *
 * Uç `summary` alanını yalnızca finans kapsamında döndürür; eski bir backend
 * ya da beklenmedik bir gövde karşısında kartlar **çizilmez** — uydurma sıfır
 * yazmak yöneticiye "bugün hiç yatırım olmamış" dedirtirdi.
 */
export const summaryView = (summary) => {
  if (!summary || typeof summary !== 'object') return null;
  const value = (key) => Number(summary[key] || 0);
  const depositIn = value('deposit_onaylandi_amount');
  const withdrawOut = value('withdraw_onaylandi_amount');
  return {
    depositIn,
    depositCount: value('deposit_onaylandi_count'),
    withdrawOut,
    withdrawCount: value('withdraw_onaylandi_count'),
    net: value('net'),
    pendingCount: value('pending_count'),
    pendingAmount: value('pending_amount'),
    pendingDepositCount: value('deposit_beklemede_count'),
    pendingDepositAmount: value('deposit_beklemede_amount'),
    pendingWithdrawCount: value('withdraw_beklemede_count'),
    pendingWithdrawAmount: value('withdraw_beklemede_amount'),
    rejectedCount: value('deposit_reddedildi_count')
      + value('withdraw_reddedildi_count'),
    approvedCount: value('deposit_onaylandi_count')
      + value('withdraw_onaylandi_count'),
    depositTotalCount: value('deposit_onaylandi_count')
      + value('deposit_beklemede_count') + value('deposit_reddedildi_count'),
    withdrawTotalCount: value('withdraw_onaylandi_count')
      + value('withdraw_beklemede_count') + value('withdraw_reddedildi_count'),
    totalCount: value('total_count'),
    // Yatırımın ne kadarı geri çekilmiş: kasanın nefes payı tek bakışta
    // okunsun diye çubuk olarak da çizilir.
    payoutRatio: depositIn > 0
      ? Math.min(1, withdrawOut / depositIn)
      : (withdrawOut > 0 ? 1 : 0),
  };
};

const TOTAL_STATE_LABELS = {
  onaylandi: 'Onaylı',
  beklemede: 'Bekleyen',
  reddedildi: 'Reddedilen',
};

/**
 * Listenin altındaki toplam satırı: süzgece uyan **bütün sayfaların** tutarı.
 *
 * Ayrı bir sorgu yoktur; özet zaten tarih, arama, sağlayıcı ve yöntem
 * penceresindeki tür/durum kırılımını taşır ve listenin ek süzgeci yalnızca
 * tür ile durumdur. Durum seçili değilse onaylı tutar yazılır — "bu
 * sağlayıcıdan ne girdi" sorusunun cevabı odur; bekleyen ya da reddedilen
 * süzgecinde o durumun toplamı yazılır.
 */
export const listTotals = (summary, tab, status) => {
  if (!summary || typeof summary !== 'object') return null;
  const state = status === 'all' ? 'onaylandi' : status;
  const kinds = tab === 'all' ? ['deposit', 'withdraw'] : [tab];
  const lines = kinds.map((kind) => ({
    kind,
    label: `${TOTAL_STATE_LABELS[state] || ''} ${kind === 'deposit' ? 'yatırım' : 'çekim'}`,
    amount: Number(summary[`${kind}_${state}_amount`] || 0),
    count: Number(summary[`${kind}_${state}_count`] || 0),
  }));
  const net = tab === 'all' && state === 'onaylandi'
    ? Number(summary.net || 0)
    : null;
  return { lines, net };
};

const Finance = ({ requestType = null }) => {
  const requestPage = FINANCE_REQUEST_PAGES[requestType] || null;
  const [searchParams] = useSearchParams();
  const { can } = useAdminPermissions();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState(() => (requestPage ? requestType : (
    financeTabs.some((item) => item.id === searchParams.get('type'))
      ? searchParams.get('type')
      : 'all'
  )));
  const [status, setStatus] = useState(() => (
    STATUS_IDS.includes(searchParams.get('status'))
      ? searchParams.get('status')
      : (requestPage ? 'beklemede' : 'all')
  ));
  const [query, setQuery] = useState('');
  const [dateRange, setDateRange] = useState(EMPTY_DATE_RANGE);
  // Sağlayıcı süzgeci liste, özet kartları ve yöntem raporuna birlikte
  // uygulanır. Yöntem süzgeci yalnızca rapordaki satıra tıklanınca kurulur
  // ve sağlayıcısıyla birlikte tutulur (aynı ad iki sağlayıcıda olabilir).
  const [provider, setProvider] = useState('all');
  const [methodFilter, setMethodFilter] = useState(null);
  const [skip, setSkip] = useState(0);
  const [pageSize, setPageSize] = useState(() => readPageSize('finance', PAGE_SIZE));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadedAt, setLoadedAt] = useState(null);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [decision, setDecision] = useState(null);
  // Red sebebi üyeye iletilir; pencere her açılışta boş başlar.
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [receiptBusy, setReceiptBusy] = useState('');
  const [reconcileBusy, setReconcileBusy] = useState('');
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [live, setLive] = useState(readStoredLive);
  // Çekim detayında gösterilen anapara çevrimi. Liste ucuna gömülseydi her
  // satır için ayrı sorgu koşardı; yalnızca panel açılınca istenir.
  const [turnover, setTurnover] = useState(null);
  const { toast } = useToast();
  // İlk yükleme iskeleti yalnızca bir kez çizilir: canlı tur her yirmi
  // saniyede listeyi iskeletle değiştirseydi ekran okunmaz hâle gelirdi.
  const firstLoadRef = useRef(true);
  // Her okumanın sıra numarası. Canlı tur yoldayken yönetici süzgeç
  // değiştirirse eski süzgecin cevabı yenisinden sonra gelebilir; yalnızca en
  // son başlatılan okumanın cevabı ekrana yazılır, yoksa liste bir an doğru
  // süzgeci gösterip eskisine geri dönerdi.
  const loadSeqRef = useRef(0);
  const lastLoadAtRef = useRef(Date.now());
  const pulseRef = useRef({ signature: null, inflight: false });

  const load = useCallback(async ({ silent = false } = {}) => {
    loadSeqRef.current += 1;
    const seq = loadSeqRef.current;
    const isLatest = () => seq === loadSeqRef.current;
    lastLoadAtRef.current = Date.now();
    if (silent) setRefreshing(true);
    else setLoading(true);
    if (!silent) setError('');
    try {
      const params = buildFinanceParams({
        skip,
        tab,
        status,
        query,
        dateRange,
        limit: pageSize,
        provider,
        method: methodFilter?.method || '',
      });
      const { data } = await client.get('/admin/transactions', { params });
      if (!isLatest()) return;
      const rows = data.items || [];
      setItems(rows);
      setTotal(data.total || 0);
      setSummary(data.summary || null);
      setLoadedAt(new Date());
      setError('');
      // Açık detay paneli listeyle birlikte tazelenir: sağlayıcı bildirimi
      // arada gelmişse yönetici eski duruma bakarak karar vermemelidir.
      setDetail((current) => (
        current ? rows.find((row) => row.id === current.id) || current : current));
    } catch (requestError) {
      if (!isLatest()) return;
      const message = requestError.response?.data?.detail
        || 'Finans işlemleri şu anda alınamıyor.';
      // Sessiz turun hatası ekrandaki listeyi silmez; yalnızca damga eskir.
      if (!silent) setError(message);
    } finally {
      if (isLatest()) {
        firstLoadRef.current = false;
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [dateRange, methodFilter, pageSize, provider, query, skip, status, tab]);

  useEffect(() => {
    const timer = setTimeout(
      () => load({ silent: !firstLoadRef.current }),
      query ? 260 : 0,
    );
    return () => clearTimeout(timer);
  }, [load, query]);

  // Canlı tur yalnızca sekme görünürken döner: arka plandaki bir panel
  // sağlayıcı webhook'larıyla aynı süreci boşuna yormamalıdır. Sekme öne
  // geldiğinde tur beklenmez, hemen döner — yönetici panele döndüğü an
  // güncel listeyi görmelidir.
  useEffect(() => {
    if (!live) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (cancelled || document.visibilityState === 'hidden') return;
      const pulse = pulseRef.current;
      if (pulse.inflight) return;
      if (Date.now() - lastLoadAtRef.current >= LIVE_REFRESH_MS) {
        load({ silent: true });
        return;
      }
      pulse.inflight = true;
      try {
        const { data } = await client.get('/admin/transactions/pulse');
        const signature = data?.signature || null;
        if (cancelled || !signature) return;
        const previous = pulse.signature;
        pulse.signature = signature;
        if (previous !== null && previous !== signature) load({ silent: true });
      } catch (_) {
        // İmza okunamazsa (ör. dağıtım sırasında eski backend) tur yine
        // seyrek tam okumayla sürer; ekrana hata yazılmaz.
      } finally {
        pulse.inflight = false;
      }
    };
    tick();
    const timer = setInterval(tick, LIVE_PULSE_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [live, load]);

  useEffect(() => () => {
    if (receiptPreview?.url) URL.revokeObjectURL(receiptPreview.url);
  }, [receiptPreview]);

  useEffect(() => {
    // Yatırımda ölçünün anlamı yok: çevrim sorusu çekimi karara bağlarken
    // sorulur.
    if (detail?.type !== 'withdraw' || !detail?.user_id) {
      setTurnover(null);
      return undefined;
    }
    let cancelled = false;
    setTurnover({ loading: true });
    client.get(`/admin/users/${detail.user_id}/turnover`)
      .then(({ data }) => { if (!cancelled) setTurnover(data); })
      .catch(() => { if (!cancelled) setTurnover({ error: true }); });
    return () => { cancelled = true; };
  }, [detail]);

  const chooseTab = (value) => {
    if (requestPage) return;
    setSkip(0);
    setTab(value);
  };

  const chooseStatus = (value) => {
    setSkip(0);
    setStatus(value);
  };

  const chooseDateRange = (value) => {
    setSkip(0);
    setDateRange(value);
  };

  const chooseQuery = (value) => {
    setSkip(0);
    setQuery(value);
  };

  const chooseProvider = (value) => {
    setSkip(0);
    setProvider(value);
    setMethodFilter(null);
  };

  // Rapordaki satır listeyi o yönteme süzer; aynı satıra ikinci tık süzgeci
  // kaldırır (rapor `null` gönderir).
  const pickMethod = (row) => {
    setSkip(0);
    if (!row) {
      setMethodFilter(null);
      return;
    }
    setProvider(row.provider);
    // Yöntem adı yazılmamış eski kayıtlar ada göre süzülemez; liste
    // sağlayıcıya süzülür, yöntem süzgeci kurulmaz.
    setMethodFilter(row.method
      ? { provider: row.provider, method: row.method }
      : null);
  };

  const toggleLive = () => {
    setLive((current) => {
      storeLive(!current);
      return !current;
    });
  };

  // Özet kartı aynı zamanda süzgeç kısayoludur; ikinci tık süzgeci kaldırır,
  // yoksa yönetici seçtiği kartı bırakmak için başka bir düğme aramak zorunda
  // kalırdı.
  const applyShortcut = (nextTab, nextStatus) => {
    const already = tab === nextTab && status === nextStatus;
    setSkip(0);
    setTab(already ? 'all' : nextTab);
    setStatus(already ? 'all' : nextStatus);
  };

  const clearFilters = () => {
    setSkip(0);
    setTab(requestPage ? requestType : 'all');
    setStatus('all');
    setQuery('');
    setDateRange(EMPTY_DATE_RANGE);
    setProvider('all');
    setMethodFilter(null);
  };

  const resolve = async () => {
    if (!decision) return;
    setBusy(true);
    try {
      const nextStatus = decision.action === 'approve' ? 'onaylandi' : 'reddedildi';
      const { data } = await client.patch(
        `/admin/transactions/${decision.item.id}`,
        {
          status: nextStatus,
          note: decision.action === 'approve'
            ? 'Yönetim panelinden onaylandı'
            : 'Yönetim panelinden reddedildi',
          reason: decision.action === 'reject' ? rejectReason.trim() : '',
        },
        { headers: { 'Idempotency-Key': createIdempotencyKey() } },
      );
      const amountText = formatMoney(Math.abs(decision.item.amount));
      // Onaylanan sağlayıcı çekimi henüz ödenmedi; talep sağlayıcıya açıldı.
      const provider = data?.provider;
      toast({
        title: provider
          ? (provider.ok === false
            ? 'Onaylandı, sağlayıcıya iletilemedi'
            : 'Onaylandı, sağlayıcıya iletildi')
          : (nextStatus === 'onaylandi' ? 'İşlem onaylandı' : 'İşlem reddedildi'),
        description: provider?.message
          || `${decision.item.username} · ${amountText}`,
      });
      setDecision(null);
      setRejectReason('');
      setDetail(null);
      await load({ silent: true });
    } catch (requestError) {
      toast({
        title: 'Karar uygulanamadı',
        description: requestError.response?.data?.detail || 'Beklenmeyen bir hata oluştu',
      });
    } finally {
      setBusy(false);
    }
  };

  // Sağlayıcının bildirimiyle kapanan kayıtlar panelden karara bağlanamaz;
  // beklemede kalan bir işlemin sonucu yalnızca durum ucundan öğrenilebilir.
  // Hangi sağlayıcının böyle bir ucu olduğu tek yerdedir (lib/financeDecision).
  const reconcile = async (item) => {
    const { reconcilePath } = financeDecision(item);
    if (!reconcilePath) return;
    setReconcileBusy(item.id);
    try {
      const { data } = await client.post(`${reconcilePath}/${item.id}`);
      // "Kayıt yok" iki farklı sonuç doğurur: çekimde tutar iade edilir,
      // yatırımda kayda **dokunulmaz** (sağlayıcı işlemi sonradan onaylayabilir
      // ve kapatılan kayıt o bildirimi çakışmaya düşürürdü). Ayrımı backend
      // `refunded` ile bildirir.
      const notFoundLabel = data.refunded
        ? 'Sağlayıcıda kayıt yok, işlem iade edildi'
        : 'Sağlayıcıda kayıt yok; yatırım açık bırakıldı, sonuç bildirimi '
          + 'hâlâ kabul edilir';
      const stateLabel = {
        SUCCESS: 'Sağlayıcı işlemi onayladı',
        FAIL: 'Sağlayıcı işlemi reddetti',
        PENDING: 'Sağlayıcıda hâlâ bekliyor',
        NOT_FOUND: notFoundLabel,
        approved: 'Sağlayıcı işlemi onayladı',
        cancelled: 'Sağlayıcı işlemi reddetti',
        pending: 'Sağlayıcıda hâlâ bekliyor',
        not_found: notFoundLabel,
        completed: 'Sağlayıcı işlemi onayladı',
        canceled: 'Sağlayıcı işlemi reddetti',
      }[data.state] || (
        // Payurus'un durum ucu terminal bir sonuç bildirmediyse kayıt
        // beklemede kalır; "sorgulandı ama değişmedi" bunu söyler.
        data.resolved === false
          ? 'Sağlayıcı henüz sonuç bildirmedi; işlem beklemede kaldı'
          : `Sağlayıcı durumu: ${data.state || 'bilinmiyor'}`
      );
      toast({ title: 'Mutabakat tamamlandı', description: stateLabel });
      setDetail(null);
      await load({ silent: true });
    } catch (requestError) {
      toast({
        title: 'Sağlayıcıdan sorgulanamadı',
        description: requestError.response?.data?.detail || 'Beklenmeyen bir hata oluştu',
      });
    } finally {
      setReconcileBusy('');
    }
  };

  const openReceipt = async (item) => {
    if (!item.receipt?.url) return;
    setReceiptBusy(item.id);
    try {
      const response = await client.get(item.receipt.url, { responseType: 'blob' });
      setReceiptPreview({
        url: URL.createObjectURL(response.data),
        name: item.receipt.original_name || 'Dekont',
        contentType: item.receipt.content_type || response.data.type,
      });
    } catch (requestError) {
      toast({
        title: 'Dekont açılamadı',
        description: requestError.response?.data?.detail || 'Dosya alınırken bir hata oluştu.',
      });
    } finally {
      setReceiptBusy('');
    }
  };

  const view = useMemo(() => summaryView(summary), [summary]);
  const totals = useMemo(
    () => listTotals(summary, tab, status), [summary, status, tab]);
  const canWrite = can('finance.write');
  const canSeeUsers = can('users.read');
  // Sayfadaki bekleyen kayıt sayısı yalnızca görünen sayfayı sayar; kuyruğun
  // tamamı özetten okunur (sayfa 2'deyken "bekleyen yok" demek yanlış olurdu).
  const pendingOnPage = items.filter((item) => item.status === 'beklemede').length;
  // Özet tür süzgecinden bağımsızdır (yalnızca tarih ve arama); talep
  // sayfası sayaçlarını kendi türünden okur.
  const typeCounts = view && requestPage ? ({
    deposit: {
      all: view.depositTotalCount,
      beklemede: view.pendingDepositCount,
      onaylandi: view.depositCount,
      reddedildi: view.depositTotalCount - view.depositCount - view.pendingDepositCount,
    },
    withdraw: {
      all: view.withdrawTotalCount,
      beklemede: view.pendingWithdrawCount,
      onaylandi: view.withdrawCount,
      reddedildi: view.withdrawTotalCount - view.withdrawCount - view.pendingWithdrawCount,
    },
  })[requestType] : null;
  const queueCount = typeCounts ? typeCounts.beklemede
    : (view ? view.pendingCount : pendingOnPage);
  const filtersActive = (!requestPage && tab !== 'all') || status !== 'all'
    || Boolean(query.trim()) || dateRange.range !== EMPTY_DATE_RANGE.range
    || provider !== 'all' || Boolean(methodFilter);
  const rangeEnd = Math.min(skip + pageSize, total);

  return (
    <div className="fin-page">
      <header className="fin-head">
        <span className="fin-head-mark" aria-hidden="true">
          {requestPage ? <requestPage.icon size={23} /> : <Coins size={23} />}
        </span>
        <div className="fin-head-copy">
          <h1>
            {requestPage ? requestPage.title : 'Finans Geçmişi'}
            <span className={`fin-queue-pill ${queueCount > 0 ? '' : 'is-clear'}`}>
              <Clock3 size={12} aria-hidden="true" />
              {queueCount > 0
                ? `${formatNumber(queueCount)} bekleyen talep`
                : 'Bekleyen talep yok'}
            </span>
          </h1>
          {requestPage ? (
            <p>
              {requestPage.description}{' '}
              Bütün hareketler <Link to="/admin/finance">Finans Geçmişi</Link>{' '}
              sayfasındadır.
            </p>
          ) : (
          <p>
            Bir ödeme yöntemiyle (API ya da elle tanımlanmış) yapılan yatırım
            ve çekimlerin tek ekranı: özet kartlar seçili tarih aralığını,
            liste ise seçili süzgeci gösterir. Karar verilebilen kayıtlarda
            onay ve ret düğmeleri satırda durur; sağlayıcının bildirimiyle
            kapanan kayıtlar yalnızca sorgulanır. Yönetici eliyle açılan
            bakiye hareketleri burada değil{' '}
            <Link to="/admin/manual-transactions">Manuel İşlemler</Link>{' '}
            sayfasındadır.
          </p>
          )}
        </div>
        <div className="fin-head-actions">
          <span
            className={`fin-stamp ${live ? 'is-live' : ''}`}
            title="Listenin son okunma saati"
          >
            <span className="fin-stamp-dot" aria-hidden="true" />
            {loadedAt ? `Son okuma ${formatStamp(loadedAt)}` : 'Okunuyor…'}
          </span>
          <button
            type="button"
            className={`fin-live ${live ? 'is-on' : ''}`}
            onClick={toggleLive}
            aria-pressed={live}
            title={live
              ? 'Canlı takip açık: yeni yatırım/çekim ve karar birkaç saniye içinde listeye düşer'
              : 'Canlı takip kapalı: liste yalnızca siz yenileyince okunur'}
          >
            <span className="fin-live-track" aria-hidden="true">
              <span className="fin-live-knob" />
            </span>
            Canlı takip
          </button>
          <button
            type="button"
            className="admin-btn-secondary"
            onClick={() => load()}
            disabled={loading || refreshing}
          >
            {refreshing || loading ? (
              <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            ) : (
              <RefreshCw size={14} aria-hidden="true" />
            )}
            Yenile
          </button>
        </div>
      </header>

      {view && !requestPage && (
        <section className="fin-kpis" aria-label="Finans özeti">
          <button
            type="button"
            className={`fin-kpi ${tab === 'deposit' && status === 'onaylandi' ? 'is-active' : ''}`}
            style={{ '--fin-kpi-tone': 'var(--fin-in)' }}
            onClick={() => applyShortcut('deposit', 'onaylandi')}
            aria-pressed={tab === 'deposit' && status === 'onaylandi'}
          >
            <div className="fin-kpi-top">
              <span className="fin-kpi-icon" aria-hidden="true">
                <ArrowDownCircle size={17} />
              </span>
              <span className="fin-kpi-name">Onaylı yatırım</span>
              <span className="fin-kpi-chip">
                {formatNumber(view.depositCount)} işlem
              </span>
            </div>
            <div>
              <div className="fin-kpi-value is-in">{formatMoney(view.depositIn)}</div>
              <div className="fin-kpi-sub">Kasaya giren onaylı para</div>
            </div>
            <div className="fin-kpi-foot">
              <div className="fin-kpi-footrow">
                <span>Bekleyen yatırım</span>
                <b>{formatMoney(view.pendingDepositAmount)}</b>
              </div>
            </div>
          </button>

          <button
            type="button"
            className={`fin-kpi ${tab === 'withdraw' && status === 'onaylandi' ? 'is-active' : ''}`}
            style={{ '--fin-kpi-tone': 'var(--fin-out)' }}
            onClick={() => applyShortcut('withdraw', 'onaylandi')}
            aria-pressed={tab === 'withdraw' && status === 'onaylandi'}
          >
            <div className="fin-kpi-top">
              <span className="fin-kpi-icon" aria-hidden="true">
                <ArrowUpCircle size={17} />
              </span>
              <span className="fin-kpi-name">Onaylı çekim</span>
              <span className="fin-kpi-chip">
                {formatNumber(view.withdrawCount)} işlem
              </span>
            </div>
            <div>
              <div className="fin-kpi-value is-out">{formatMoney(view.withdrawOut)}</div>
              <div className="fin-kpi-sub">Kasadan çıkan onaylı para</div>
            </div>
            <div className="fin-kpi-foot">
              <div className="fin-kpi-footrow">
                <span>Bekleyen çekim</span>
                <b>{formatMoney(view.pendingWithdrawAmount)}</b>
              </div>
            </div>
          </button>

          <div className="fin-kpi" style={{ '--fin-kpi-tone': 'var(--accent-primary)' }}>
            <div className="fin-kpi-top">
              <span className="fin-kpi-icon" aria-hidden="true">
                <Scale size={17} />
              </span>
              <span className="fin-kpi-name">Net kasa</span>
              <span className={`fin-kpi-chip ${view.net < 0 ? 'is-down' : 'is-up'}`}>
                {view.net < 0 ? 'Çıkış fazla' : 'Giriş fazla'}
              </span>
            </div>
            <div>
              <div className="fin-kpi-value">{formatMoney(view.net)}</div>
              <div className="fin-kpi-sub">
                Onaylı yatırım eksi onaylı çekim; bonus ve oyun kârı bu rakamda
                yoktur.
              </div>
            </div>
            <div className="fin-kpi-foot">
              <div className="fin-kpi-footrow">
                <span>Yatırımın çekilen payı</span>
                <b>%{Math.round(view.payoutRatio * 100)}</b>
              </div>
              <div className="fin-kpi-bar" aria-hidden="true">
                <span style={{ width: `${Math.round(view.payoutRatio * 100)}%` }} />
              </div>
            </div>
          </div>

          <button
            type="button"
            className={`fin-kpi ${status === 'beklemede' && tab === 'all' ? 'is-active' : ''}`}
            style={{ '--fin-kpi-tone': 'var(--fin-wait)' }}
            onClick={() => applyShortcut('all', 'beklemede')}
            aria-pressed={status === 'beklemede' && tab === 'all'}
          >
            <div className="fin-kpi-top">
              <span className="fin-kpi-icon" aria-hidden="true">
                <Clock3 size={17} />
              </span>
              <span className="fin-kpi-name">Bekleyen kuyruk</span>
              <span className="fin-kpi-chip">
                {formatNumber(view.pendingCount)} talep
              </span>
            </div>
            <div>
              <div className="fin-kpi-value is-wait">{formatMoney(view.pendingAmount)}</div>
              <div className="fin-kpi-sub">Karara bağlanmayı bekleyen tutar</div>
            </div>
            <div className="fin-kpi-foot">
              <div className="fin-kpi-footrow">
                <span>Yatırım · Çekim</span>
                <b>
                  {formatNumber(view.pendingDepositCount)}
                  {' · '}
                  {formatNumber(view.pendingWithdrawCount)}
                </b>
              </div>
            </div>
          </button>
        </section>
      )}

      {!requestPage && (
        <FinanceMethodReport
          provider={provider}
          tab={tab}
          dateParams={dateRangeParams(dateRange)}
          refreshToken={loadedAt}
          activeMethod={methodFilter}
          onPick={pickMethod}
        />
      )}

      <section className="fin-filters" aria-label="Süzgeçler">
        <div className="fin-filter-row">
          <span className="fin-filter-label">{requestPage ? 'Ara' : 'Tür'}</span>
          {!requestPage && (
          <div className="fin-seg" role="group" aria-label="İşlem türü">
            {financeTabs.map((item) => {
              const count = !view ? null : ({
                all: view.totalCount,
                deposit: view.depositTotalCount,
                withdraw: view.withdrawTotalCount,
              })[item.id];
              return (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => chooseTab(item.id)}
                  className={`fin-seg-btn ${tab === item.id ? 'is-active' : ''}`}
                  aria-pressed={tab === item.id}
                >
                  {item.label}
                  {count !== null && count !== undefined && (
                    <span className="fin-seg-count">{formatNumber(count)}</span>
                  )}
                </button>
              );
            })}
          </div>
          )}
          <div className="fin-search">
            <label htmlFor="finance-search" className="sr-only">
              Finans işlemlerinde ara
            </label>
            <Search size={15} aria-hidden="true" />
            <input
              id="finance-search"
              value={query}
              onChange={(event) => chooseQuery(event.target.value)}
              className="admin-input"
              placeholder="Kullanıcı adı veya ödeme yöntemi ara"
            />
            {query && (
              <button
                type="button"
                className="fin-search-clear"
                onClick={() => chooseQuery('')}
                aria-label="Aramayı temizle"
                title="Aramayı temizle"
              >
                <X size={12} aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        <div className="fin-filter-row">
          <span className="fin-filter-label">Durum</span>
          <div className="fin-seg" role="group" aria-label="İşlem durumu">
            {financeStatuses.map((item) => {
              const count = !view ? null : (typeCounts || {
                all: view.totalCount,
                beklemede: view.pendingCount,
                onaylandi: view.approvedCount,
                reddedildi: view.rejectedCount,
              })[item.id];
              return (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => chooseStatus(item.id)}
                  className={`fin-seg-btn ${item.tone} ${status === item.id ? 'is-active' : ''}`}
                  aria-pressed={status === item.id}
                >
                  {item.label}
                  {count !== null && count !== undefined && (
                    <span className="fin-seg-count">{formatNumber(count)}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tarih süzgeci ayrı satırdadır: serbest aralık seçildiğinde iki
            tarih girişi açılır ve tür/durum süzgeçleriyle aynı satırda
            sıkışıp okunmaz hâle geliyordu. */}
        <div className="fin-filter-row">
          <label className="fin-filter-label" htmlFor="finance-provider">
            Sağlayıcı
          </label>
          <select
            id="finance-provider"
            className="admin-input admin-input-w-lg"
            value={provider}
            onChange={(event) => chooseProvider(event.target.value)}
          >
            <option value="all">Tüm sağlayıcılar</option>
            {FINANCE_PROVIDER_OPTIONS.map((item) => (
              <option key={item.id} value={item.id}>{item.label}</option>
            ))}
          </select>
        </div>

        <div className="fin-filter-row">
          <span className="fin-filter-label">Tarih</span>
          <DateRangeFilter value={dateRange} onChange={chooseDateRange} />
        </div>

        {filtersActive && (
          <div className="fin-filter-row">
            <span className="fin-filter-label">Etkin</span>
            <div className="fin-chips">
              {tab !== 'all' && !requestPage && (
                <span className="fin-chip">
                  {financeTabs.find((item) => item.id === tab)?.label}
                  <button
                    type="button"
                    onClick={() => chooseTab('all')}
                    aria-label="Tür süzgecini kaldır"
                  >
                    <X size={10} aria-hidden="true" />
                  </button>
                </span>
              )}
              {status !== 'all' && (
                <span className="fin-chip">
                  {financeStatuses.find((item) => item.id === status)?.label}
                  <button
                    type="button"
                    onClick={() => chooseStatus('all')}
                    aria-label="Durum süzgecini kaldır"
                  >
                    <X size={10} aria-hidden="true" />
                  </button>
                </span>
              )}
              {query.trim() && (
                <span className="fin-chip">
                  {`Arama: ${query.trim()}`}
                  <button
                    type="button"
                    onClick={() => chooseQuery('')}
                    aria-label="Aramayı kaldır"
                  >
                    <X size={10} aria-hidden="true" />
                  </button>
                </span>
              )}
              {provider !== 'all' && (
                <span className="fin-chip">
                  {financeProviderLabel(provider)}
                  <button
                    type="button"
                    onClick={() => chooseProvider('all')}
                    aria-label="Sağlayıcı süzgecini kaldır"
                  >
                    <X size={10} aria-hidden="true" />
                  </button>
                </span>
              )}
              {methodFilter && (
                <span className="fin-chip">
                  {`Yöntem: ${methodFilter.method || 'Yöntem adı yok'}`}
                  <button
                    type="button"
                    onClick={() => pickMethod(null)}
                    aria-label="Yöntem süzgecini kaldır"
                  >
                    <X size={10} aria-hidden="true" />
                  </button>
                </span>
              )}
              {dateRange.range !== EMPTY_DATE_RANGE.range && (
                <span className="fin-chip">
                  Tarih aralığı
                  <button
                    type="button"
                    onClick={() => chooseDateRange(EMPTY_DATE_RANGE)}
                    aria-label="Tarih süzgecini kaldır"
                  >
                    <X size={10} aria-hidden="true" />
                  </button>
                </span>
              )}
              <button type="button" className="fin-chips-clear" onClick={clearFilters}>
                Süzgeçleri temizle
              </button>
            </div>
          </div>
        )}
      </section>

      {error ? (
        <AdminError message={error} onRetry={() => load()} />
      ) : (
        <section className="fin-list" aria-label="Finans hareketleri">
          <div className="fin-list-head">
            <span className="fin-list-title">
              <Wallet size={15} aria-hidden="true" />
              Hareketler
            </span>
            <span className="fin-list-count">
              {loading ? 'Okunuyor…' : `${formatNumber(total)} kayıt`}
            </span>
            {view && !requestPage && (
              <span className="fin-list-count fin-pager-spacer">
                Özet kartlar seçili tarih ve aramayı gösterir; tür ile durum
                yalnızca listeyi süzer.
              </span>
            )}
          </div>

          {loading ? (
            <div
              className="fin-skeleton"
              role="status"
              aria-label="Finans hareketleri yükleniyor"
            >
              <span className="sr-only">Finans hareketleri yükleniyor</span>
              {Array.from({ length: 8 }, (_, index) => (
                <div className="fin-skeleton-row" key={index}>
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="fin-state-box">
              <Inbox size={28} aria-hidden="true" />
              <h2>Finans hareketi bulunamadı</h2>
              <p>
                {filtersActive
                  ? 'Seçili süzgeçlere uyan kayıt yok. Süzgeçleri temizleyip '
                    + 'tüm hareketlere bakabilirsiniz.'
                  : 'Bu kurulumda henüz yatırım veya çekim talebi oluşmamış.'}
              </p>
              {filtersActive && (
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={clearFilters}
                >
                  Süzgeçleri temizle
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="fin-scroll">
                <table className="fin-table">
                  <thead>
                    <tr>
                      <th>Kullanıcı</th>
                      <th>Tür</th>
                      <th>Yöntem</th>
                      <th className="is-right">Tutar</th>
                      <th>Durum</th>
                      <th>İşlemi yapan</th>
                      <th>Tarih</th>
                      <th className="is-right">
                        <span className="sr-only">İşlemler</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const Icon = iconMap[item.type] || ArrowLeftRight;
                      const displayNote = visibleTransactionNote(item);
                      const actor = transactionActor(item);
                      const pending = item.status === 'beklemede';
                      const ability = financeDecision(item);
                      const incoming = item.amount >= 0;
                      const providerNote = ability.providerManaged
                        ? `${ability.providerLabel}${item.provider_status
                          ? ` · ${PROVIDER_STATE_LABELS[item.provider_status] || item.provider_status}`
                          : ''}`
                        : '';
                      return (
                        <tr key={item.id} className={pending ? 'is-pending' : ''}>
                          <td className="fin-cell-user" data-label="Kullanıcı">
                            <div className="fin-user">
                              <span className="fin-avatar" aria-hidden="true">
                                {(item.username || '?').slice(0, 1).toLocaleUpperCase('tr-TR')}
                              </span>
                              <span className="min-w-0">
                                {canSeeUsers && item.user_id ? (
                                  <Link
                                    to={`/admin/users/${item.user_id}`}
                                    className="fin-user-name"
                                    title={`${item.username} üyesinin profilini aç`}
                                  >
                                    {item.username}
                                  </Link>
                                ) : (
                                  <span className="fin-user-name">{item.username}</span>
                                )}
                                {item.balance_after === null
                                  || item.balance_after === undefined ? null : (
                                    <span className="fin-user-meta">
                                      {`İşlem sonrası ${formatMoney(item.balance_after)}`}
                                    </span>
                                  )}
                              </span>
                            </div>
                          </td>
                          <td className="fin-cell-type" data-label="Tür">
                            <span className={`fin-type ${item.type}`}>
                              <Icon size={13} aria-hidden="true" />
                              {transactionTypeLabel[item.type] || item.type}
                            </span>
                          </td>
                          <td className="fin-cell-method" data-label="Yöntem">
                            <div className="fin-method">
                              <span className="fin-method-name" title={item.method || ''}>
                                {item.method || 'Yöntem belirtilmedi'}
                              </span>
                              {displayNote && (
                                <span className="fin-note" title={displayNote}>
                                  {item.type === 'deposit' && item.receipt && (
                                    <FileText size={11} aria-hidden="true" />
                                  )}
                                  {displayNote}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="fin-cell-amount" data-label="Tutar">
                            <span className={`fin-amount ${incoming ? 'is-in' : 'is-out'}`}>
                              {incoming ? '+' : '−'}
                              {formatMoney(Math.abs(item.amount))}
                            </span>
                          </td>
                          <td className="fin-cell-state" data-label="Durum">
                            <div>
                              <span className={`fin-state ${stateToneMap[item.status] || 'is-wait'}`}>
                                {transactionStatusLabel[item.status] || item.status}
                              </span>
                              {providerNote && (
                                <span className="fin-provider" title={providerNote}>
                                  {providerNote}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="fin-cell-actor" data-label="İşlemi yapan">
                            {actor ? (
                              <span className="fin-actor" title={`İşlemi yapan yetkili: ${actor}`}>
                                {actor}
                              </span>
                            ) : (
                              <span
                                className="fin-actor is-empty"
                                title="Üye talebi veya sağlayıcı hareketi; yetkili işlemi değil"
                              >
                                —
                              </span>
                            )}
                          </td>
                          <td className="fin-cell-date" data-label="Tarih">
                            <span className="fin-date">{formatDateTime(item.created_at)}</span>
                          </td>
                          <td className="fin-cell-actions" data-label="İşlemler">
                            <div className="fin-rowactions">
                              {/* Çekim kararı verilirken üyenin oynayıp oynamadığına
                                  bakılır; sayfayı bırakmamak için yeni sekmede açılır. */}
                              {item.user_id && (
                                <a
                                  className="fin-act is-icon"
                                  href={`/admin/users/${item.user_id}/games`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={`${item.username} üyesinin bahis geçmişini yeni sekmede aç`}
                                  aria-label={`${item.username} üyesinin bahis geçmişini yeni sekmede aç`}
                                >
                                  <Gamepad2 size={14} aria-hidden="true" />
                                </a>
                              )}
                              {canWrite && ability.canResolve && (
                                <>
                                  <button
                                    type="button"
                                    className="fin-act is-reject is-icon"
                                    onClick={() => setDecision({ action: 'reject', item })}
                                    title="Talebi reddet"
                                    aria-label={`${item.username} talebini reddet`}
                                  >
                                    <XCircle size={14} aria-hidden="true" />
                                  </button>
                                  <button
                                    type="button"
                                    className="fin-act is-approve is-icon"
                                    onClick={() => setDecision({ action: 'approve', item })}
                                    title="Talebi onayla"
                                    aria-label={`${item.username} talebini onayla`}
                                  >
                                    <CheckCircle2 size={14} aria-hidden="true" />
                                  </button>
                                </>
                              )}
                              {canWrite && !ability.canResolve && ability.canReconcile && (
                                <button
                                  type="button"
                                  className="fin-act is-sync is-icon"
                                  onClick={() => reconcile(item)}
                                  disabled={reconcileBusy === item.id}
                                  title={`Durumu ${ability.providerLabel} tarafından sorgula`}
                                  aria-label={`${item.username} işlemini sağlayıcıdan sorgula`}
                                >
                                  {reconcileBusy === item.id ? (
                                    <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                                  ) : (
                                    <RefreshCw size={14} aria-hidden="true" />
                                  )}
                                </button>
                              )}
                              <button
                                type="button"
                                className={`fin-act is-detail ${pending ? 'is-pending' : ''}`}
                                onClick={() => setDetail(item)}
                                aria-label={`${item.username} işleminin detayını aç`}
                              >
                                <span className="fin-act-label">
                                  {pending ? 'İncele' : 'Detay'}
                                </span>
                                <ChevronRight size={14} aria-hidden="true" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {totals && (
                    <tfoot>
                      <tr className="fin-total-row">
                        <th scope="row" colSpan={3} className="fin-total-label">
                          Toplam
                          <span>
                            {provider === 'all'
                              ? 'Tüm sağlayıcılar'
                              : financeProviderLabel(provider)}
                            {methodFilter?.method ? ` · ${methodFilter.method}` : ''}
                            {` · süzgece uyan ${formatNumber(total)} kayıt, bütün sayfalar`}
                          </span>
                        </th>
                        <td className="fin-total-amount" colSpan={5} data-label="Toplam">
                          {/* Flex iç kutudadır: hücrenin kendisine verilseydi
                              tablo hücresi olmaktan çıkar, colSpan yok sayılır
                              ve tutarlar dar bir sütuna sıkışırdı. */}
                          <div className="fin-total-lines">
                          {totals.lines.map((line) => (
                            <span className="fin-total-line" key={line.kind}>
                              <span className="fin-total-name">
                                {line.label}
                                {` · ${formatNumber(line.count)} işlem`}
                              </span>
                              <span
                                className={`fin-amount ${line.kind === 'deposit' ? 'is-in' : 'is-out'}`}
                              >
                                {line.kind === 'deposit' ? '+' : '−'}
                                {formatMoney(line.amount)}
                              </span>
                            </span>
                          ))}
                          {totals.net !== null && (
                            <span className="fin-total-line is-net">
                              <span className="fin-total-name">Net</span>
                              <span className="fin-amount">{formatMoney(totals.net)}</span>
                            </span>
                          )}
                          </div>
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              <div className="fin-pager">
                <button
                  type="button"
                  disabled={skip === 0}
                  onClick={() => setSkip(Math.max(0, skip - pageSize))}
                  className="admin-btn-secondary"
                >
                  <ChevronLeft size={14} aria-hidden="true" />
                  Önceki
                </button>
                <span className="fin-pager-info" aria-live="polite">
                  <b>{formatNumber(skip + 1)}</b>
                  {' – '}
                  <b>{formatNumber(rangeEnd)}</b>
                  {` / ${formatNumber(total)} kayıt`}
                  {pendingOnPage > 0 && ` · bu sayfada ${formatNumber(pendingOnPage)} bekleyen`}
                </span>
                <PageSizeSelect
                  value={pageSize}
                  disabled={loading}
                  onChange={(size) => {
                    setSkip(0);
                    setPageSize(size);
                    writePageSize('finance', size);
                  }}
                />
                <button
                  type="button"
                  disabled={skip + pageSize >= total}
                  onClick={() => setSkip(skip + pageSize)}
                  className="admin-btn-secondary fin-pager-spacer"
                >
                  Sonraki
                  <ChevronRight size={14} aria-hidden="true" />
                </button>
              </div>
            </>
          )}
        </section>
      )}

      <TransactionDetailDrawer
        item={detail}
        onClose={() => setDetail(null)}
        onDecision={(action) => setDecision({ action, item: detail })}
        onOpenReceipt={openReceipt}
        onReconcile={reconcile}
        canWrite={canWrite}
        turnover={turnover}
        reconcileBusy={Boolean(detail) && reconcileBusy === detail.id}
        receiptBusy={Boolean(detail) && receiptBusy === detail.id}
        escapeDisabled={Boolean(decision) || Boolean(receiptPreview)}
      />

      {receiptPreview && (
        <div
          className="fin-layer is-receipt"
          role="dialog"
          aria-modal="true"
          aria-label="Yatırım dekontu"
        >
          <button
            type="button"
            className="fin-backdrop"
            aria-label="Dekont önizlemesini kapat"
            onClick={() => setReceiptPreview(null)}
          />
          <div className="fin-receipt">
            <div className="fin-receipt-head">
              <FileText size={17} aria-hidden="true" />
              <h2>{receiptPreview.name}</h2>
              <a
                href={receiptPreview.url}
                download={receiptPreview.name}
                className="fin-act"
                title="Dekontu indir"
              >
                <Download size={14} aria-hidden="true" />
                <span className="fin-act-label">İndir</span>
              </a>
              <button
                type="button"
                className="fin-close"
                onClick={() => setReceiptPreview(null)}
                title="Kapat"
                aria-label="Dekont önizlemesini kapat"
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>
            <div className="fin-receipt-body">
              {receiptPreview.contentType.startsWith('image/') ? (
                <img src={receiptPreview.url} alt={receiptPreview.name} />
              ) : (
                <iframe src={receiptPreview.url} title={receiptPreview.name} />
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(decision)}
        title={decision?.action === 'approve' ? 'Finans işlemini onayla' : 'Finans işlemini reddet'}
        description={decision
          ? `${decision.item.username} kullanıcısının ${formatMoney(Math.abs(decision.item.amount))} tutarındaki ${transactionTypeLabel[decision.item.type]?.toLocaleLowerCase('tr-TR') || 'işlem'} kaydı ${decision.action === 'approve' ? 'onaylanacak' : 'reddedilecek'}.${payoutDescription(decision.item)}${providerHandoffNote(decision)}`
          : ''}
        confirmLabel={decision?.action === 'approve' ? 'Onayla' : 'Reddet'}
        tone={decision?.action === 'approve' ? 'warning' : 'danger'}
        busy={busy}
        extra={decision?.action === 'reject' ? (
          <div className="fin-reject-reason">
            <label htmlFor="fin-reject-reason">
              Red sebebi <span>(isteğe bağlı)</span>
            </label>
            <textarea
              id="fin-reject-reason"
              name="reject_reason"
              autoComplete="off"
              aria-describedby="fin-reject-hint"
              className="admin-input"
              rows={3}
              maxLength={REJECT_REASON_MAX}
              value={rejectReason}
              disabled={busy}
              onChange={(event) => setRejectReason(event.target.value)}
              placeholder="Örn. Çevrim şartı tamamlanmadı…"
            />
            <div className="fin-reject-presets" aria-label="Hazır sebepler">
              {REJECT_REASON_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className="fin-reject-preset"
                  disabled={busy}
                  aria-pressed={rejectReason.trim() === preset}
                  onClick={() => setRejectReason(preset)}
                >
                  {preset}
                </button>
              ))}
            </div>
            <p id="fin-reject-hint" className="fin-reject-hint">
              {rejectReason.trim()
                ? 'Sebep üyenin Mesajlarım kutusuna ve talep listesine yazılır, Telegram bildirimine eklenir.'
                : 'Boş bırakılırsa üyeye mesaj gönderilmez.'}
            </p>
          </div>
        ) : null}
        onCancel={() => {
          if (busy) return;
          setDecision(null);
          setRejectReason('');
        }}
        onConfirm={resolve}
      />
    </div>
  );
};

export default Finance;
